import { useState, useEffect, useRef, useCallback } from "react";
import {
  MessageSquare, Phone, Mic, MicOff, Send, Search, Users, Star,
  Trophy, Award, Zap, Crown, TrendingUp, Clock, Check, CheckCheck,
  X, ChevronRight, ChevronDown, Smile, Paperclip, Image, MapPin,
  Volume2, VolumeX, Play, Pause, RefreshCw, Sparkles, Brain,
  MessageCircle, PhoneCall, PhoneOff, Settings, Filter, Hash,
  Bell, BellOff, MoreVertical, Copy, Trash2, Pin, Flag, Heart, Loader2
} from "lucide-react";
import { toast } from "sonner";
import getApiUrl from '../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// ═══════════════════════════════════════════════════════════════
// API FUNCTIONS
// ═══════════════════════════════════════════════════════════════
const api = {
  async getCollaborateurs() {
    try {
      const res = await authFetch(`${API}/sania-connect/collaborateurs`);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      return data.collaborateurs || [];
    } catch (e) {
      console.error('API getCollaborateurs error:', e);
      return null; // Return null to trigger fallback
    }
  },
  
  async getConversation(collabId) {
    try {
      const res = await authFetch(`${API}/sania-connect/conversations/${collabId}`);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      return data.messages || [];
    } catch (e) {
      console.error('API getConversation error:', e);
      return null;
    }
  },
  
  async sendMessage(collabId, text, channel, isVoice = false) {
    try {
      const res = await authFetch(`${API}/sania-connect/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          collaborateur_id: collabId,
          text,
          channel,
          is_voice: isVoice
        })
      });
      if (!res.ok) throw new Error('Failed to send');
      return await res.json();
    } catch (e) {
      console.error('API sendMessage error:', e);
      return null;
    }
  },
  
  async sendVoiceMessage(collabId, audioBlob) {
    try {
      const formData = new FormData();
      formData.append('collaborateur_id', collabId);
      formData.append('audio', audioBlob, 'voice.webm');
      
      const res = await authFetch(`${API}/sania-connect/messages/voice`, {
        method: 'POST',
        body: formData
      });
      if (!res.ok) throw new Error('Failed to send voice');
      return await res.json();
    } catch (e) {
      console.error('API sendVoiceMessage error:', e);
      return null;
    }
  },
  
  async getChristelleSuggestions(collabId) {
    try {
      const res = await authFetch(`${API}/sania-connect/christelle/suggestions/${collabId}`);
      if (!res.ok) throw new Error('Failed to fetch');
      return await res.json();
    } catch (e) {
      console.error('API getChristelleSuggestions error:', e);
      return null;
    }
  },
  
  async getChristelleSummary(collabId) {
    try {
      const res = await authFetch(`${API}/sania-connect/christelle/summary/${collabId}`);
      if (!res.ok) throw new Error('Failed to fetch');
      return await res.json();
    } catch (e) {
      console.error('API getChristelleSummary error:', e);
      return null;
    }
  },
  
  async getLeaderboard() {
    try {
      const res = await authFetch(`${API}/sania-connect/leaderboard`);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      return data.leaderboard || [];
    } catch (e) {
      console.error('API getLeaderboard error:', e);
      return null;
    }
  },
  
  async getUserStats(userId) {
    try {
      const res = await authFetch(`${API}/sania-connect/stats/${userId}`);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      return data.stats;
    } catch (e) {
      console.error('API getUserStats error:', e);
      return null;
    }
  },
  
  async seedData() {
    try {
      const res = await authFetch(`${API}/sania-connect/seed`, { method: 'POST' });
      return await res.json();
    } catch (e) {
      console.error('API seedData error:', e);
      return null;
    }
  },
  
  // Address Book APIs
  async getAddressBook(search = '', source = 'all') {
    try {
      const params = new URLSearchParams({ search, source, limit: '100' });
      const res = await authFetch(`${API}/sania-connect/address-book?${params}`);
      if (!res.ok) throw new Error('Failed to fetch');
      return await res.json();
    } catch (e) {
      console.error('API getAddressBook error:', e);
      return null;
    }
  },
  
  async addFromContact(contactId, source, customRole = null) {
    try {
      const params = new URLSearchParams({ contact_id: contactId, source });
      if (customRole) params.append('custom_role', customRole);
      
      const res = await authFetch(`${API}/sania-connect/collaborateurs/from-contact?${params}`, {
        method: 'POST'
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to add');
      }
      return await res.json();
    } catch (e) {
      console.error('API addFromContact error:', e);
      throw e;
    }
  },
  
  async addExternalContact(name, phone, role = null) {
    try {
      const res = await authFetch(`${API}/sania-connect/collaborateurs/external`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, phone, role })
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to add');
      }
      return await res.json();
    } catch (e) {
      console.error('API addExternalContact error:', e);
      throw e;
    }
  },
  
  async deleteCollaborateur(collabId) {
    try {
      const res = await authFetch(`${API}/sania-connect/collaborateurs/${collabId}`, {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error('Failed to delete');
      return await res.json();
    } catch (e) {
      console.error('API deleteCollaborateur error:', e);
      return null;
    }
  }
};

// ═══════════════════════════════════════════════════════════════
// CONSTANTES & CONFIGURATION
// ═══════════════════════════════════════════════════════════════
const CHANNELS = {
  whatsapp: { id: 'whatsapp', label: 'WhatsApp', icon: '💬', color: 'emerald', bgClass: 'bg-emerald-500' },
  sms: { id: 'sms', label: 'SMS', icon: '📱', color: 'blue', bgClass: 'bg-blue-500' },
  vocal: { id: 'vocal', label: 'Vocal', icon: '🎤', color: 'purple', bgClass: 'bg-purple-500' },
  ringover: { id: 'ringover', label: 'Appel', icon: '📞', color: 'orange', bgClass: 'bg-orange-500' }
};

const BADGES = [
  { id: 'speed_demon', name: 'Speed Demon', icon: '⚡', description: 'Réponse < 2min', color: 'yellow', points: 50 },
  { id: 'early_bird', name: 'Early Bird', icon: '🌅', description: 'Actif avant 8h', color: 'orange', points: 30 },
  { id: 'night_owl', name: 'Night Owl', icon: '🦉', description: 'Actif après 20h', color: 'indigo', points: 30 },
  { id: 'helper', name: 'Super Helper', icon: '🤝', description: '50 messages utiles', color: 'emerald', points: 100 },
  { id: 'streak_7', name: 'Streak 7', icon: '🔥', description: '7 jours consécutifs', color: 'red', points: 70 },
  { id: 'streak_30', name: 'Streak Master', icon: '💎', description: '30 jours consécutifs', color: 'cyan', points: 200 },
  { id: 'voice_master', name: 'Voice Master', icon: '🎙️', description: '100 messages vocaux', color: 'purple', points: 80 },
  { id: 'communicator', name: 'Grand Communicator', icon: '👑', description: '500 messages envoyés', color: 'amber', points: 150 },
];

// Fallback data when API is unavailable
const MOCK_COLLABORATEURS = [
  { id: '1', _id: '1', name: 'Sophie Martin', role: 'Télépro', avatar: 'SM', online: true, lastSeen: 'En ligne', phone: '+33612345678', points: 1250, badges: ['speed_demon', 'helper'], streak: 12 },
  { id: '2', _id: '2', name: 'Marc Dubois', role: 'Technicien R1', avatar: 'MD', online: true, lastSeen: 'En ligne', phone: '+33623456789', points: 980, badges: ['early_bird', 'streak_7'], streak: 8 },
  { id: '3', _id: '3', name: 'Julie Leroy', role: 'AMO', avatar: 'JL', online: false, lastSeen: 'Il y a 15min', phone: '+33634567890', points: 1450, badges: ['communicator', 'helper', 'streak_30'], streak: 32 },
  { id: '4', _id: '4', name: 'Thomas Bernard', role: 'Administratif', avatar: 'TB', online: false, lastSeen: 'Il y a 1h', phone: '+33645678901', points: 720, badges: ['night_owl'], streak: 3 },
  { id: '5', _id: '5', name: 'Marie Petit', role: 'Direction', avatar: 'MP', online: true, lastSeen: 'En ligne', phone: '+33656789012', points: 2100, badges: ['communicator', 'streak_30', 'speed_demon', 'helper'], streak: 45 },
  { id: '6', _id: '6', name: 'Éric Laurent', role: 'Chantiers', avatar: 'EL', online: false, lastSeen: 'Il y a 3h', phone: '+33667890123', points: 560, badges: ['voice_master'], streak: 5 },
];

const MOCK_CONVERSATIONS = {
  '1': [
    { id: 1, sender: 'Sophie Martin', senderId: '1', text: 'Salut ! Tu as vu le nouveau lead qui vient d\'arriver ?', time: '09:15', channel: 'whatsapp', read: true },
    { id: 2, sender: 'Moi', senderId: 'me', text: 'Oui je viens de le voir ! Il a l\'air très qualifié', time: '09:16', channel: 'whatsapp', read: true },
    { id: 3, sender: 'Sophie Martin', senderId: '1', text: 'Exactement, il habite dans le 75 et a déjà fait sa simulation', time: '09:17', channel: 'whatsapp', read: true },
    { id: 4, sender: 'Sophie Martin', senderId: '1', text: 'Tu peux prendre le RDV R1 ?', time: '09:18', channel: 'whatsapp', read: false },
  ],
  '2': [
    { id: 1, sender: 'Marc Dubois', senderId: '2', text: 'RDV R1 terminé chez Mme Dupont', time: '14:30', channel: 'sms', read: true },
    { id: 2, sender: 'Marc Dubois', senderId: '2', text: 'Dossier éligible MaPrimeAdapt !', time: '14:31', channel: 'sms', read: true },
    { id: 3, sender: 'Moi', senderId: 'me', text: 'Super ! Tu peux m\'envoyer les photos ?', time: '14:35', channel: 'sms', read: true },
  ],
  '3': [
    { id: 1, sender: 'Julie Leroy', senderId: '3', text: 'Le dossier ANAH est validé 🎉', time: '11:00', channel: 'whatsapp', read: true },
    { id: 2, sender: 'Moi', senderId: 'me', text: 'Génial ! Montant accordé ?', time: '11:02', channel: 'whatsapp', read: true },
    { id: 3, sender: 'Julie Leroy', senderId: '3', text: '15 000€ de subvention', time: '11:03', channel: 'whatsapp', read: true },
  ],
};

const CANAUX_THEMATIQUES = [
  { id: 'urgences', name: '🚨 Urgences', color: 'red', unread: 2 },
  { id: 'wins', name: '🏆 Wins', color: 'yellow', unread: 5 },
  { id: 'telepro', name: '📞 Télépro', color: 'blue', unread: 0 },
  { id: 'chantiers', name: '🏗️ Chantiers', color: 'orange', unread: 1 },
  { id: 'general', name: '💬 Général', color: 'slate', unread: 3 },
];

// ═══════════════════════════════════════════════════════════════
// COMPOSANT: Badge Affichage
// ═══════════════════════════════════════════════════════════════
const BadgeDisplay = ({ badgeId, size = 'sm' }) => {
  const badge = BADGES.find(b => b.id === badgeId);
  if (!badge) return null;
  
  const sizeClasses = {
    sm: 'w-6 h-6 text-sm',
    md: 'w-8 h-8 text-base',
    lg: 'w-10 h-10 text-lg'
  };
  
  return (
    <div 
      className={`${sizeClasses[size]} rounded-full bg-${badge.color}-500/20 flex items-center justify-center`}
      title={`${badge.name}: ${badge.description}`}
    >
      {badge.icon}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COMPOSANT: Assistant IA Christelle
// ═══════════════════════════════════════════════════════════════
const ChristelleAssistant = ({ collabId, context, onSuggestionClick, onClose }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [summary, setSummary] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [activeTab, setActiveTab] = useState('suggestions');
  const [isRealAI, setIsRealAI] = useState(false);

  useEffect(() => {
    loadSuggestions();
  }, [collabId, context]);

  const loadSuggestions = async () => {
    if (!collabId) return;
    
    setIsThinking(true);
    
    // Try real API first
    const result = await api.getChristelleSuggestions(collabId);
    
    if (result && result.suggestions) {
      setSuggestions(result.suggestions.map((text, i) => ({ id: i + 1, text, type: 'ai' })));
      setIsRealAI(true);
    } else {
      // Fallback to mock suggestions
      setSuggestions([
        { id: 1, text: "D'accord, je m'en occupe tout de suite !", type: 'positive' },
        { id: 2, text: "Pouvez-vous me donner plus de détails ?", type: 'question' },
        { id: 3, text: "Je vérifie et je vous tiens informé.", type: 'action' },
        { id: 4, text: "Parfait, merci pour l'info ! 👍", type: 'acknowledgment' },
      ]);
      setIsRealAI(false);
    }
    
    setIsThinking(false);
  };

  const loadSummary = async () => {
    if (!collabId) return;
    
    setIsThinking(true);
    setActiveTab('resume');
    
    const result = await api.getChristelleSummary(collabId);
    
    if (result && result.summary) {
      setSummary(result.summary);
      setIsRealAI(true);
    } else {
      setSummary("Conversation sur un nouveau lead qualifié. Demande de prise de RDV R1. Le prospect a déjà fait sa simulation MaPrimeAdapt.");
      setIsRealAI(false);
    }
    
    setIsThinking(false);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'resume' && !summary) {
      loadSummary();
    } else if (tab === 'suggestions' && suggestions.length === 0) {
      loadSuggestions();
    }
  };

  return (
    <div className="bg-gradient-to-br from-purple-900/50 to-pink-900/50 border border-purple-500/30 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h4 className="font-semibold text-white text-sm flex items-center gap-2">
              Christelle IA
              {isRealAI && <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full">GPT-5.2</span>}
            </h4>
            <p className="text-xs text-purple-300">Assistant intelligent</p>
          </div>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-white/10 rounded">
          <X className="w-4 h-4 text-slate-400" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-3">
        {['suggestions', 'resume', 'transcription'].map(tab => (
          <button
            key={tab}
            onClick={() => handleTabChange(tab)}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
              activeTab === tab 
                ? 'bg-purple-500 text-white' 
                : 'bg-slate-700/50 text-slate-400 hover:text-white'
            }`}
          >
            {tab === 'suggestions' ? '💡 Suggestions' : tab === 'resume' ? '📝 Résumé' : '🎤 Transcription'}
          </button>
        ))}
      </div>

      {isThinking ? (
        <div className="flex items-center gap-2 py-4">
          <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
          <span className="text-sm text-purple-300">Christelle analyse avec GPT-5.2...</span>
        </div>
      ) : activeTab === 'suggestions' ? (
        <div className="space-y-2">
          {suggestions.map(suggestion => (
            <button
              key={suggestion.id}
              onClick={() => onSuggestionClick(suggestion.text)}
              className="w-full text-left p-2 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700/50 hover:border-purple-500/50 transition-all group"
            >
              <span className="text-sm text-white group-hover:text-purple-300">{suggestion.text}</span>
            </button>
          ))}
        </div>
      ) : activeTab === 'resume' ? (
        <div className="p-3 bg-slate-800/50 rounded-lg">
          <p className="text-sm text-slate-300">{summary || "Chargement du résumé..."}</p>
        </div>
      ) : (
        <div className="p-3 bg-slate-800/50 rounded-lg">
          <p className="text-sm text-slate-400 italic">Envoyez un message vocal pour voir la transcription automatique via Whisper</p>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COMPOSANT: Enregistreur Vocal
// ═══════════════════════════════════════════════════════════════
const VoiceRecorder = ({ onRecordComplete, onCancel }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isRecording) {
      intervalRef.current = setInterval(() => {
        setDuration(d => d + 1);
        setAudioLevel(Math.random() * 100);
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRecording]);

  const startRecording = () => {
    setIsRecording(true);
    setDuration(0);
  };

  const stopRecording = () => {
    setIsRecording(false);
    clearInterval(intervalRef.current);
    // Simuler l'envoi
    onRecordComplete({
      duration,
      transcription: "Message vocal transcrit par Whisper IA..."
    });
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="bg-purple-900/30 border border-purple-500/30 rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {isRecording ? (
            <>
              <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
              <span className="text-white font-mono">{formatDuration(duration)}</span>
              {/* Visualisation audio */}
              <div className="flex items-center gap-0.5 h-6">
                {[...Array(10)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1 bg-purple-500 rounded-full transition-all"
                    style={{ height: `${Math.random() * audioLevel}%`, minHeight: '4px' }}
                  />
                ))}
              </div>
            </>
          ) : (
            <span className="text-slate-400">Appuyez pour enregistrer</span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {isRecording ? (
            <>
              <button
                onClick={onCancel}
                className="p-2 bg-slate-700 hover:bg-slate-600 rounded-full"
              >
                <X className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={stopRecording}
                className="p-3 bg-red-500 hover:bg-red-600 rounded-full"
              >
                <MicOff className="w-5 h-5 text-white" />
              </button>
            </>
          ) : (
            <button
              onClick={startRecording}
              className="p-3 bg-purple-500 hover:bg-purple-600 rounded-full"
            >
              <Mic className="w-5 h-5 text-white" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COMPOSANT: Classement Gamification
// ═══════════════════════════════════════════════════════════════
const LeaderboardPanel = ({ collaborateurs, onClose }) => {
  const sortedCollab = [...collaborateurs].sort((a, b) => b.points - a.points);

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
      <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 p-4 border-b border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Trophy className="w-6 h-6 text-yellow-400" />
            <h3 className="font-bold text-white">Classement</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
      </div>
      
      <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
        {sortedCollab.map((collab, index) => (
          <div
            key={collab.id}
            className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
              index === 0 ? 'bg-yellow-500/10 border border-yellow-500/30' :
              index === 1 ? 'bg-slate-400/10 border border-slate-400/30' :
              index === 2 ? 'bg-orange-500/10 border border-orange-500/30' :
              'bg-slate-700/30'
            }`}
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
              index === 0 ? 'bg-yellow-500 text-black' :
              index === 1 ? 'bg-slate-400 text-black' :
              index === 2 ? 'bg-orange-500 text-white' :
              'bg-slate-600 text-white'
            }`}>
              {index + 1}
            </div>
            
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
              {collab.avatar}
            </div>
            
            <div className="flex-1">
              <p className="font-medium text-white">{collab.name}</p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">{collab.role}</span>
                <span className="text-xs text-orange-400">🔥 {collab.streak}j</span>
              </div>
            </div>
            
            <div className="text-right">
              <p className="font-bold text-white">{collab.points.toLocaleString()}</p>
              <p className="text-xs text-slate-400">points</p>
            </div>
            
            <div className="flex gap-1">
              {collab.badges.slice(0, 3).map(badgeId => (
                <BadgeDisplay key={badgeId} badgeId={badgeId} size="sm" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COMPOSANT: Carnet d'Adresses Modal
// ═══════════════════════════════════════════════════════════════
const AddressBookModal = ({ onClose, onContactAdded }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [adding, setAdding] = useState(null);
  const [stats, setStats] = useState({ users: 0, clients: 0 });
  const [activeTab, setActiveTab] = useState('carnet'); // 'carnet' ou 'externe'
  
  // External contact form
  const [externalName, setExternalName] = useState('');
  const [externalPhone, setExternalPhone] = useState('');
  const [externalRole, setExternalRole] = useState('');
  const [addingExternal, setAddingExternal] = useState(false);

  useEffect(() => {
    if (activeTab === 'carnet') {
      loadContacts();
    }
  }, [sourceFilter, activeTab]);

  const loadContacts = async () => {
    setLoading(true);
    const result = await api.getAddressBook(searchTerm, sourceFilter);
    if (result) {
      setContacts(result.contacts || []);
      setStats(result.sources || { users: 0, clients: 0 });
    }
    setLoading(false);
  };

  const handleSearch = () => {
    loadContacts();
  };

  const handleAddContact = async (contact) => {
    setAdding(contact.id);
    try {
      await api.addFromContact(contact.id, contact.source);
      toast.success(`${contact.name} ajouté aux collaborateurs !`);
      onContactAdded();
      // Update local state
      setContacts(prev => prev.map(c => 
        c.id === contact.id ? { ...c, already_added: true } : c
      ));
    } catch (e) {
      toast.error(e.message || 'Erreur lors de l\'ajout');
    }
    setAdding(null);
  };

  const handleAddExternal = async () => {
    if (!externalName.trim() || !externalPhone.trim()) {
      toast.error('Nom et téléphone requis');
      return;
    }
    
    setAddingExternal(true);
    try {
      await api.addExternalContact(externalName, externalPhone, externalRole || null);
      toast.success(`${externalName} ajouté avec succès !`);
      onContactAdded();
      // Reset form
      setExternalName('');
      setExternalPhone('');
      setExternalRole('');
      onClose();
    } catch (e) {
      toast.error(e.message || 'Erreur lors de l\'ajout');
    }
    setAddingExternal(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 p-4 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-white">Ajouter un Contact</h3>
                <p className="text-sm text-slate-400">
                  Carnet d'adresses ou numéro externe
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-700">
          <button
            onClick={() => setActiveTab('carnet')}
            className={`flex-1 px-4 py-3 font-medium transition-colors ${
              activeTab === 'carnet'
                ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-500/10'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            📋 Carnet d'Adresses
          </button>
          <button
            onClick={() => setActiveTab('externe')}
            className={`flex-1 px-4 py-3 font-medium transition-colors ${
              activeTab === 'externe'
                ? 'text-emerald-400 border-b-2 border-emerald-400 bg-emerald-500/10'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            📱 Numéro Externe
          </button>
        </div>

        {activeTab === 'carnet' ? (
          <>
            {/* Search & Filters */}
            <div className="p-4 border-b border-slate-700 space-y-3">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Rechercher par nom ou téléphone..."
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-10 pr-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                >
                  Rechercher
                </button>
              </div>
              
              {/* Source Filters */}
              <div className="flex gap-2">
                {[
                  { id: 'all', label: '📋 Tous', color: 'slate' },
                  { id: 'users', label: '👤 Équipe CRM', color: 'blue' },
                  { id: 'clients', label: '🏠 Clients', color: 'emerald' }
                ].map(filter => (
                  <button
                    key={filter.id}
                    onClick={() => setSourceFilter(filter.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      sourceFilter === filter.id
                        ? `bg-${filter.color}-500 text-white`
                        : 'bg-slate-700 text-slate-400 hover:text-white'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-400" />
                  <span className="ml-2 text-slate-400">Chargement...</span>
                </div>
              ) : contacts.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 mx-auto mb-3 text-slate-600" />
                  <p className="text-slate-400">Aucun contact trouvé</p>
                </div>
              ) : (
                contacts.map(contact => (
                  <div
                    key={`${contact.source}-${contact.id}`}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                      contact.already_added
                        ? 'bg-slate-700/30 border-slate-700/50 opacity-60'
                        : 'bg-slate-700/50 border-slate-600/50 hover:border-blue-500/50'
                    }`}
                  >
                    {/* Avatar */}
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                      contact.source === 'users' 
                        ? 'bg-gradient-to-br from-blue-500 to-indigo-500'
                        : 'bg-gradient-to-br from-emerald-500 to-teal-500'
                    }`}>
                      {contact.avatar}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-white truncate">{contact.name}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          contact.source === 'users'
                            ? 'bg-blue-500/20 text-blue-300'
                            : 'bg-emerald-500/20 text-emerald-300'
                        }`}>
                          {contact.source === 'users' ? 'Équipe' : 'Client'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-slate-400">
                        {contact.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {contact.phone}
                          </span>
                        )}
                        {contact.role && contact.role !== 'Client' && (
                          <span>{contact.role}</span>
                        )}
                      </div>
                    </div>

                    {/* Add Button */}
                    {contact.already_added ? (
                      <span className="px-3 py-1.5 bg-slate-600 text-slate-400 rounded-lg text-sm">
                        ✓ Ajouté
                      </span>
                    ) : (
                      <button
                        onClick={() => handleAddContact(contact)}
                        disabled={adding === contact.id}
                        className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-1"
                      >
                        {adding === contact.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <span>+</span> Ajouter
                          </>
                        )}
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          /* External Contact Form */
          <div className="flex-1 p-6 space-y-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-3">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-lg font-semibold text-white">Ajouter un numéro externe</h4>
              <p className="text-sm text-slate-400 mt-1">Contact qui n'est pas dans votre CRM</p>
            </div>
            
            <div className="space-y-4">
              {/* Nom */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Nom complet *
                </label>
                <input
                  type="text"
                  value={externalName}
                  onChange={(e) => setExternalName(e.target.value)}
                  placeholder="Ex: Jean Dupont"
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                />
              </div>
              
              {/* Téléphone */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Numéro de téléphone *
                </label>
                <input
                  type="tel"
                  value={externalPhone}
                  onChange={(e) => setExternalPhone(e.target.value)}
                  placeholder="Ex: 06 12 34 56 78"
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500"
                />
              </div>
              
              {/* Rôle (optionnel) */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Rôle / Fonction (optionnel)
                </label>
                <select
                  value={externalRole}
                  onChange={(e) => setExternalRole(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-emerald-500"
                >
                  <option value="">Sélectionner...</option>
                  <option value="Fournisseur">Fournisseur</option>
                  <option value="Partenaire">Partenaire</option>
                  <option value="Prestataire">Prestataire</option>
                  <option value="Artisan">Artisan</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Client potentiel">Client potentiel</option>
                  <option value="Ami/Famille">Ami / Famille</option>
                  <option value="Contact externe">Autre</option>
                </select>
              </div>
            </div>
            
            {/* Submit Button */}
            <button
              onClick={handleAddExternal}
              disabled={addingExternal || !externalName.trim() || !externalPhone.trim()}
              className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex items-center justify-center gap-2"
            >
              {addingExternal ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Ajout en cours...
                </>
              ) : (
                <>
                  <Users className="w-5 h-5" />
                  Ajouter ce contact
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COMPOSANT: Zone de Conversation
// ═══════════════════════════════════════════════════════════════
const ConversationArea = ({ 
  selectedCollab, 
  messages, 
  onSendMessage, 
  activeChannel, 
  setActiveChannel,
  onStartCall 
}) => {
  const [newMessage, setNewMessage] = useState('');
  const [showChristelle, setShowChristelle] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!newMessage.trim()) return;
    onSendMessage({
      text: newMessage,
      channel: activeChannel,
      time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    });
    setNewMessage('');
    // Simuler réponse
    setIsTyping(true);
    setTimeout(() => setIsTyping(false), 2000);
  };

  const handleVoiceComplete = (voiceData) => {
    setShowVoiceRecorder(false);
    onSendMessage({
      text: `🎤 Message vocal (${voiceData.duration}s)`,
      channel: 'vocal',
      time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      isVoice: true,
      transcription: voiceData.transcription
    });
    toast.success('Message vocal envoyé et transcrit !');
  };

  if (!selectedCollab) {
    return (
      <div className="flex-1 flex items-center justify-center bg-slate-800/30">
        <div className="text-center">
          <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-600" />
          <p className="text-slate-400">Sélectionnez une conversation</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-slate-800/30">
      {/* Header conversation */}
      <div className="p-4 border-b border-slate-700 bg-slate-800/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg">
                {selectedCollab.avatar}
              </div>
              {selectedCollab.online && (
                <div className="absolute bottom-0 right-0 w-4 h-4 bg-emerald-500 rounded-full border-2 border-slate-800" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-white">{selectedCollab.name}</h3>
              <p className="text-sm text-slate-400">{selectedCollab.lastSeen}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Sélection de canal */}
            {Object.values(CHANNELS).map(channel => (
              <button
                key={channel.id}
                onClick={() => channel.id === 'ringover' ? onStartCall(selectedCollab) : setActiveChannel(channel.id)}
                className={`p-2 rounded-lg transition-colors ${
                  activeChannel === channel.id 
                    ? `${channel.bgClass} text-white` 
                    : 'bg-slate-700 text-slate-400 hover:text-white'
                }`}
                title={channel.label}
              >
                {channel.id === 'whatsapp' && <MessageCircle className="w-5 h-5" />}
                {channel.id === 'sms' && <MessageSquare className="w-5 h-5" />}
                {channel.id === 'vocal' && <Mic className="w-5 h-5" />}
                {channel.id === 'ringover' && <PhoneCall className="w-5 h-5" />}
              </button>
            ))}
            
            <button
              onClick={() => setShowChristelle(!showChristelle)}
              className={`p-2 rounded-lg transition-colors ${
                showChristelle 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-slate-700 text-slate-400 hover:text-white'
              }`}
              title="Assistant Christelle IA"
            >
              <Sparkles className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Assistant Christelle */}
      {showChristelle && (
        <div className="p-4 border-b border-slate-700">
          <ChristelleAssistant
            collabId={selectedCollab._id || selectedCollab.id}
            context={messages}
            onSuggestionClick={(text) => setNewMessage(text)}
            onClose={() => setShowChristelle(false)}
          />
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.senderId === 'me' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[70%] ${msg.senderId === 'me' ? 'order-2' : ''}`}>
              <div
                className={`rounded-2xl px-4 py-2 ${
                  msg.senderId === 'me'
                    ? 'bg-purple-500 text-white rounded-br-sm'
                    : 'bg-slate-700 text-white rounded-bl-sm'
                }`}
              >
                {msg.isVoice ? (
                  <div className="flex items-center gap-2">
                    <button className="p-1 bg-white/20 rounded-full">
                      <Play className="w-4 h-4" />
                    </button>
                    <div className="flex-1">
                      <div className="h-1 bg-white/30 rounded-full">
                        <div className="h-full w-1/3 bg-white rounded-full" />
                      </div>
                    </div>
                    <span className="text-xs opacity-70">{msg.text}</span>
                  </div>
                ) : (
                  <p>{msg.text}</p>
                )}
                {msg.isVoice && msg.transcription && (
                  <p className="text-xs mt-1 opacity-70 italic">"{msg.transcription}"</p>
                )}
              </div>
              <div className={`flex items-center gap-1 mt-1 ${msg.senderId === 'me' ? 'justify-end' : ''}`}>
                <span className="text-xs text-slate-500">{msg.time}</span>
                <span className="text-xs">{CHANNELS[msg.channel]?.icon}</span>
                {msg.senderId === 'me' && (
                  msg.read ? <CheckCheck className="w-3 h-3 text-blue-400" /> : <Check className="w-3 h-3 text-slate-500" />
                )}
              </div>
            </div>
          </div>
        ))}
        
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-slate-700 rounded-2xl px-4 py-2 rounded-bl-sm">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input zone */}
      <div className="p-4 border-t border-slate-700 bg-slate-800/50">
        {showVoiceRecorder ? (
          <VoiceRecorder
            onRecordComplete={handleVoiceComplete}
            onCancel={() => setShowVoiceRecorder(false)}
          />
        ) : (
          <div className="flex items-center gap-3">
            <button className="p-2 text-slate-400 hover:text-white transition-colors">
              <Paperclip className="w-5 h-5" />
            </button>
            <button className="p-2 text-slate-400 hover:text-white transition-colors">
              <Image className="w-5 h-5" />
            </button>
            <button className="p-2 text-slate-400 hover:text-white transition-colors">
              <MapPin className="w-5 h-5" />
            </button>
            
            <div className="flex-1 relative">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder={`Message via ${CHANNELS[activeChannel]?.label || 'WhatsApp'}...`}
                className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500"
              />
              <button className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                <Smile className="w-5 h-5" />
              </button>
            </div>
            
            <button
              onClick={() => setShowVoiceRecorder(true)}
              className="p-2 bg-purple-500/20 text-purple-400 hover:bg-purple-500 hover:text-white rounded-xl transition-colors"
            >
              <Mic className="w-5 h-5" />
            </button>
            
            <button
              onClick={handleSend}
              disabled={!newMessage.trim()}
              className="p-2 bg-purple-500 text-white rounded-xl hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COMPOSANT: Appel en cours (Ringover)
// ═══════════════════════════════════════════════════════════════
const ActiveCallPanel = ({ collab, onEndCall }) => {
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeaker, setIsSpeaker] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => setDuration(d => d + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-3xl p-8 w-full max-w-md border border-slate-700">
        <div className="text-center">
          <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-3xl font-bold mb-4 animate-pulse">
            {collab.avatar}
          </div>
          <h2 className="text-2xl font-bold text-white mb-1">{collab.name}</h2>
          <p className="text-slate-400 mb-2">{collab.role}</p>
          <p className="text-emerald-400 text-lg font-mono">{formatDuration(duration)}</p>
        </div>

        <div className="flex justify-center gap-4 mt-8">
          <button
            onClick={() => setIsMuted(!isMuted)}
            className={`p-4 rounded-full ${isMuted ? 'bg-red-500' : 'bg-slate-700'} transition-colors`}
          >
            {isMuted ? <MicOff className="w-6 h-6 text-white" /> : <Mic className="w-6 h-6 text-white" />}
          </button>
          
          <button
            onClick={() => setIsSpeaker(!isSpeaker)}
            className={`p-4 rounded-full ${isSpeaker ? 'bg-purple-500' : 'bg-slate-700'} transition-colors`}
          >
            {isSpeaker ? <Volume2 className="w-6 h-6 text-white" /> : <VolumeX className="w-6 h-6 text-white" />}
          </button>
          
          <button
            onClick={onEndCall}
            className="p-4 rounded-full bg-red-500 hover:bg-red-600 transition-colors"
          >
            <PhoneOff className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL: SAN-IA Connect
// ═══════════════════════════════════════════════════════════════
export default function SaniaConnect({ onBack }) {
  const [collaborateurs, setCollaborateurs] = useState(MOCK_COLLABORATEURS);
  const [conversations, setConversations] = useState(MOCK_CONVERSATIONS);
  const [selectedCollab, setSelectedCollab] = useState(null);
  const [activeChannel, setActiveChannel] = useState('whatsapp');
  const [searchTerm, setSearchTerm] = useState('');
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showAddressBook, setShowAddressBook] = useState(false);
  const [loading, setLoading] = useState(true);
  const [usingRealAPI, setUsingRealAPI] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [selectedThematicChannel, setSelectedThematicChannel] = useState(null);
  const [myStats, setMyStats] = useState({ points: 1580, rank: 2, streak: 15, badges: ['speed_demon', 'helper', 'streak_7'] });
  const [sendingMessage, setSendingMessage] = useState(false);

  const loadCollaborateurs = async () => {
    const collabs = await api.getCollaborateurs();
    if (collabs && collabs.length > 0) {
      setCollaborateurs(collabs.map(c => ({
        ...c,
        id: c._id || c.id,
        lastSeen: c.online ? 'En ligne' : 'Hors ligne'
      })));
      setUsingRealAPI(true);
    } else {
      setCollaborateurs(MOCK_COLLABORATEURS);
      setUsingRealAPI(false);
    }
  };

  // Load collaborateurs on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
      // Try to seed data first (will skip if already seeded)
      await api.seedData();
      
      // Load collaborateurs
      await loadCollaborateurs();
      
      setLoading(false);
    };
    
    loadData();
  }, []);

  // Load conversation when collab is selected
  useEffect(() => {
    const loadConversation = async () => {
      if (!selectedCollab) return;
      
      const collabId = selectedCollab._id || selectedCollab.id;
      const messages = await api.getConversation(collabId);
      
      if (messages && messages.length > 0) {
        setConversations(prev => ({
          ...prev,
          [collabId]: messages.map(m => ({
            ...m,
            id: m._id || m.id,
            time: m.created_at ? new Date(m.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : m.time
          }))
        }));
      }
    };
    
    loadConversation();
  }, [selectedCollab]);

  const handleSendMessage = async (message) => {
    if (!selectedCollab) return;
    
    const collabId = selectedCollab._id || selectedCollab.id;
    setSendingMessage(true);
    
    // Optimistic update
    const newMsg = {
      id: Date.now(),
      sender: 'Moi',
      senderId: 'me',
      text: message.text,
      time: message.time,
      channel: message.channel,
      read: false,
      isVoice: message.isVoice,
      transcription: message.transcription
    };
    
    setConversations(prev => ({
      ...prev,
      [collabId]: [...(prev[collabId] || []), newMsg]
    }));
    
    // Send to API
    const result = await api.sendMessage(collabId, message.text, message.channel, message.isVoice);
    
    if (result && result.send_result?.success) {
      toast.success(`Message envoyé via ${CHANNELS[message.channel]?.label} ✓`);
      
      // Update gamification
      if (result.gamification?.points_added) {
        setMyStats(prev => ({
          ...prev,
          points: prev.points + result.gamification.points_added
        }));
      }
    } else if (result) {
      toast.success(`Message enregistré (envoi ${CHANNELS[message.channel]?.label} en attente)`);
    } else {
      toast.info(`Message ajouté localement`);
    }
    
    setSendingMessage(false);
  };

  const handleStartCall = (collab) => {
    setActiveCall(collab);
    toast.success(`Appel en cours avec ${collab.name}...`);
    
    // Trigger Ringover SDK click-to-call if available
    if (window.ringover && collab.phone) {
      window.ringover.dial(collab.phone);
    }
  };

  const filteredCollabs = collaborateurs.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="max-w-full mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors text-slate-400 hover:text-white"
              >
                <ChevronRight className="w-5 h-5 rotate-180" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 via-purple-500 to-pink-500 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white flex items-center gap-2">
                    SAN-IA Connect
                    {usingRealAPI && <span className="text-xs bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full">API Live</span>}
                  </h1>
                  <p className="text-sm text-slate-400">Hub de communication collaborateurs</p>
                </div>
              </div>
            </div>

            {/* Stats personnelles */}
            <div className="flex items-center gap-4">
              {/* Bouton Ajouter */}
              <button
                onClick={() => setShowAddressBook(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                <Users className="w-4 h-4" />
                <span className="font-medium">+ Ajouter</span>
              </button>
              
              <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <Star className="w-4 h-4 text-yellow-400" />
                <span className="font-bold text-yellow-400">{myStats.points}</span>
                <span className="text-xs text-slate-400">pts</span>
              </div>
              
              <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                <Zap className="w-4 h-4 text-orange-400" />
                <span className="font-bold text-orange-400">{myStats.streak}</span>
                <span className="text-xs text-slate-400">jours</span>
              </div>
              
              <button
                onClick={() => setShowLeaderboard(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg hover:opacity-90 transition-opacity"
              >
                <Trophy className="w-4 h-4" />
                <span className="font-medium">#{myStats.rank}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar gauche - Canaux thématiques */}
        <div className="w-16 bg-slate-900 border-r border-slate-700 flex flex-col items-center py-4 gap-2">
          {CANAUX_THEMATIQUES.map(canal => (
            <button
              key={canal.id}
              onClick={() => setSelectedThematicChannel(canal.id)}
              className={`relative w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                selectedThematicChannel === canal.id
                  ? `bg-${canal.color}-500 text-white`
                  : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
              }`}
              title={canal.name}
            >
              <span className="text-lg">{canal.name.split(' ')[0]}</span>
              {canal.unread > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs font-bold flex items-center justify-center text-white">
                  {canal.unread}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Liste des collaborateurs */}
        <div className="w-80 bg-slate-800/50 border-r border-slate-700 flex flex-col">
          <div className="p-4 border-b border-slate-700">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher..."
                className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-10 pr-4 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 text-sm"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {filteredCollabs.map(collab => {
              const lastMsg = conversations[collab.id]?.slice(-1)[0];
              const unreadCount = conversations[collab.id]?.filter(m => !m.read && m.senderId !== 'me').length || 0;
              
              return (
                <div
                  key={collab.id}
                  onClick={() => setSelectedCollab(collab)}
                  className={`p-3 border-b border-slate-700/50 cursor-pointer transition-colors ${
                    selectedCollab?.id === collab.id
                      ? 'bg-purple-500/10 border-l-2 border-l-purple-500'
                      : 'hover:bg-slate-700/30'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold">
                        {collab.avatar}
                      </div>
                      {collab.online && (
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 rounded-full border-2 border-slate-800" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-white truncate">{collab.name}</h4>
                        {lastMsg && (
                          <span className="text-xs text-slate-500">{lastMsg.time}</span>
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-slate-400 truncate">
                          {lastMsg ? lastMsg.text : collab.role}
                        </p>
                        {unreadCount > 0 && (
                          <span className="w-5 h-5 bg-purple-500 rounded-full text-xs font-bold flex items-center justify-center text-white">
                            {unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Badges */}
                  <div className="flex gap-1 mt-2">
                    {collab.badges.slice(0, 4).map(badgeId => (
                      <BadgeDisplay key={badgeId} badgeId={badgeId} size="sm" />
                    ))}
                    <div className="flex items-center gap-1 ml-auto">
                      <Star className="w-3 h-3 text-yellow-400" />
                      <span className="text-xs text-slate-400">{collab.points}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Zone de conversation */}
        <ConversationArea
          selectedCollab={selectedCollab}
          messages={selectedCollab ? (conversations[selectedCollab.id] || []) : []}
          onSendMessage={handleSendMessage}
          activeChannel={activeChannel}
          setActiveChannel={setActiveChannel}
          onStartCall={handleStartCall}
        />
      </div>

      {/* Modal Leaderboard */}
      {showLeaderboard && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-lg">
            <LeaderboardPanel
              collaborateurs={collaborateurs}
              onClose={() => setShowLeaderboard(false)}
            />
          </div>
        </div>
      )}

      {/* Modal Carnet d'Adresses */}
      {showAddressBook && (
        <AddressBookModal
          onClose={() => setShowAddressBook(false)}
          onContactAdded={() => {
            loadCollaborateurs();
          }}
        />
      )}

      {/* Appel en cours */}
      {activeCall && (
        <ActiveCallPanel
          collab={activeCall}
          onEndCall={() => {
            setActiveCall(null);
            toast.success('Appel terminé');
          }}
        />
      )}
    </div>
  );
}
